<template>
  <div class="organization container-fluid">
    <div class="row header-row">
      <div class="col-12 col-md-4 col-lg-6">
        <h1>Cable Test Results</h1>
      </div>
    </div>
    <section class="main-content">
      <div class="filter">
        <h5>
          Filter
        </h5>
        <form-component>
          <form
            id="test-results-filter"
            class="test-results-filter row"
            novalidate="novalidate"
            @submit.prevent
          >
            <div class="col-sm-6 col-md-4 col-lg-3 filter-item">
              <input-text
                id="start_date"
                v-model="selectedStartDate"
                name="start_date"
                data-vv-name="start_date"
                data-vv-as="Start Date"
                label="Start Date"
                type="date"
              />
            </div>
            <div class="col-sm-6 col-md-4 col-lg-3 filter-item">
              <input-text
                id="end_date"
                v-model="selectedEndDate"
                name="end_date"
                data-vv-name="end_date"
                data-vv-as="End Date"
                label="End Date"
                :max="maxEndDate"
                type="date"
              />
            </div>
            <div class="col-sm-6 col-md-4 col-lg-3 filter-item">
              <input-multi-select
                id="serial"
                v-model="selectedSerialNumber"
                name="serial"
                data-vv-name="serial"
                data-vv-as="Serial Number"
                label="Serial Number"
                key-field="key"
                display-field="label"
                :options="devicesSelectOptions"
              />
            </div>
            <div class="col-sm-6 col-md-4 col-lg-3 filter-item">
              <input-text
                id="selectedCableSerialNumber"
                v-model="selectedCableSerialNumber"
                name="selectedCableSerialNumber"
                data-vv-name="selectedCableSerialNumber"
                data-vv-as="Cable SN"
                label="Cable SN"
              />
            </div>
            <div class="col-sm-6 col-md-4 col-lg-3 filter-item">
              <input-text
                id="selectedTechnicianId"
                v-model="selectedTechnicianId"
                name="selectedTechnicianId"
                data-vv-name="selectedTechnicianId"
                data-vv-as="Technician Id"
                label="Technician Id"
              />
            </div>
            <div class="col-sm-6 col-md-4 col-lg-3 filter-item">
              <input-select
                id="overallResult"
                v-model="selectedOverallResult"
                name="overallResult"
                data-vv-name="overallResult"
                data-vv-as="Overall Status"
                label="Overall Status"
                key-field="key"
                display-field="label"
                :options="testResultOptions"
              />
            </div>
            <div class="col-sm-6 col-md-4 col-lg-3 d-flex align-items-center button-area">
              <b-button-group style="width: 100%;">
                <b-button
                  variant="primary"
                  class="btn btn--primary"
                  type="submit"
                  @click="updateFilter()"
                >
                  Filter
                </b-button>
                <b-button
                  variant="light"
                  class="btn"
                  type="button"
                  @click="resetFilter()"
                >
                  Clear Filter
                </b-button>
                <b-dropdown
                  variant="primary"
                  :disabled="exportInProgress"
                >
                  <template #button-content>
                    <span
                      :class="{'invisible': exportInProgress}"
                    >
                      Export
                    </span>
                    <span
                      v-if="exportInProgress"
                      class="inline-loader"
                    >
                      <Loader />
                    </span>
                  </template>
                  <b-dropdown-item @click="exportReport('csv')">
                    CSV
                  </b-dropdown-item>
                  <b-dropdown-item @click="exportReport('xlsx')">
                    Excel
                  </b-dropdown-item>
                  <b-dropdown-item @click="exportReport('pdf')">
                    PDF
                  </b-dropdown-item>
                </b-dropdown>
              </b-button-group>
            </div>
          </form>
        </form-component>
      </div>

      <div
        v-if="!notYetFiltered"
        class="data"
      >
        <div
          v-if="!testResults"
          class="col-12"
        >
          <Loader />
        </div>
        <div
          v-else-if="testResults.length === 0"
          class="col-12"
        >
          <div class="no-results text-center">
            <div class="vertical-center">
              No Results Found
            </div>
          </div>
        </div>
        <div
          v-else
        >
          <div class="table-data">
            <responsive-table>
              <test-fixture-results-table
                :report-data="testResults"
                :download-file="downloadFile"
                @update-sort="handleUpdateSort"
              />
            </responsive-table>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import Loader from '@/components/Loader.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputMultiSelect from '@/components/inputs/InputMultiSelect.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';
import TestFixtureResultsTable from '@/components/tables/TestFixtureResultsTable.vue';


const dateToFormattedString = (dateValue) => {
  const offset = dateValue.getTimezoneOffset();
  const offsetDate = new Date(dateValue.getTime() + (offset * 60 * 1000));
  return offsetDate.toISOString().split('T')[0];
};

export default {
  name: 'TestFixtureResults',
  components: {
    Loader,
    FormComponent,
    InputText,
    InputSelect,
    InputMultiSelect,
    TestFixtureResultsTable,
    ResponsiveTable,
  },

  data() {
    const dt = new Date();
    return {
      userClient: new UserClient(this.$http),
      devices: [],
      testResults: [],
      notYetFiltered: true,
      selectedSortObject: {},
      exportInProgress: false,

      selectedSerialNumber: '',
      selectedStartDate: '',
      selectedEndDate: '',
      selectedOverallResult: '',
      selectedCableSerialNumber: '',
      selectedTechnicianId: '',
      maxEndDate: `${dt.getUTCFullYear()}-${(`0${dt.getUTCMonth() + 1}`).slice(-2)}-${(`0${dt.getUTCDate()}`).slice(-2)}`,
      testResultOptions: [
      ],
    };
  },
  computed: {
    devicesSelectOptions() {
      if (!this.devices) {
        return [];
      }

      return this.devices
        .map(x => ({
          key: `${x.id}`,
          label: x.serialNumber,
        }));
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params': function () {
      const previousString = JSON.stringify(this.buildFilteredRequest());
      this.handleRouteValues({ ...this.$route.query });
      if (previousString !== JSON.stringify(this.buildFilteredRequest())) {
        this.getTestResultsData();
      }
    },
  },
  mounted() {
    this.getDevices();

    this.defaultValues();
    this.handleRouteValues(this.$route.query);
    this.updateFilter(false);
  },
  methods: {
    getDevices() {
      this.devices = [];

      this.userClient.getTestFixtures().then((devices) => {
        this.devices = devices;
      }).catch(() => {
        this.devices = [];
      });
    },
    handleRouteValues(queryParameterObject) {
      const currentDate = new Date();
      const previousMonth = new Date(currentDate);
      previousMonth.setMonth(previousMonth.getMonth() - 1);

      this.selectedSerialNumber = (queryParameterObject.testFixtures) ? queryParameterObject.testFixtures : 'all';
      this.selectedStartDate = (queryParameterObject.startDate)
        ? queryParameterObject.startDate : dateToFormattedString(previousMonth);
      this.selectedEndDate = (queryParameterObject.endDate)
        ? queryParameterObject.endDate : dateToFormattedString(currentDate);
      this.selectedCableSerialNumber = (queryParameterObject.cableSerialNumber) ? queryParameterObject.cableSerialNumber : '';
      this.selectedTechnicianId = (queryParameterObject.technicianId) ? queryParameterObject.technicianId : '';
      this.selectedOverallResult = (queryParameterObject.overallResult) ? queryParameterObject.overallResult : 'all';

      this.testResultOptions = [
        { key: 'all', label: 'ALL' },
        { key: 'PASS', label: 'PASS' },
        { key: 'FAIL', label: 'FAIL' },
      ];
    },
    /** Builds a filter request used by filtering data AND exporting */
    buildFilteredRequest() {
      return {
        testFixtures: (this.selectedSerialNumber !== 'all') ? this.selectedSerialNumber : undefined,
        startDate: (this.selectedStartDate && new Date(this.selectedStartDate))
          ? dateToFormattedString(new Date(this.selectedStartDate)) : undefined,
        endDate: (this.selectedEndDate && new Date(this.selectedEndDate))
          ? dateToFormattedString(new Date(this.selectedEndDate)) : undefined,
        cableSerialNumber: (this.selectedCableSerialNumber)
          ? this.selectedCableSerialNumber.trim() : undefined,
        technicianId: (this.selectedTechnicianId) ? this.selectedTechnicianId.trim() : undefined,
        overallResult: (this.selectedOverallResult !== 'all') ? this.selectedOverallResult : undefined,

      };
    },
    getTestResultsData() {
      this.testResults = null;
      this.userClient.getTestFixtureResults(this.buildFilteredRequest()).then((testResultsRes) => {
        this.testResults = testResultsRes.results;
      }).catch(() => {
        this.testResults = null;
      });
    },
    updateFilter(updateRouter = true) {
      this.notYetFiltered = false;
      this.selectedSortObject = {};
      this.getTestResultsData();
      if (updateRouter) {
        this.$router.push({ query: this.buildFilteredRequest() });
      }
    },
    downloadFile(testResultId) {
      this.userClient.downloadTestFixtureResultFile(testResultId);
    },
    handleUpdateSort(event) {
      this.selectedSortObject = event;
    },
    defaultValues() {
      const currentDate = new Date();
      const previousMonth = new Date(currentDate);
      previousMonth.setMonth(previousMonth.getMonth() - 1);
      this.selectedSortObject = {};
      this.notYetFiltered = true;
      this.selectedSerialNumber = 'all';
      this.selectedStartDate = dateToFormattedString(previousMonth);
      this.selectedEndDate = dateToFormattedString(currentDate);
      this.selectedCableSerialNumber = '';
      this.selectedTechnicianId = '';
      this.overallResult = 'all';
    },
    resetFilter() {
      this.defaultValues();
      this.updateFilter();
    },
    exportReport(filterType) {
      this.exportInProgress = true;
      this.userClient.getTestFixtureResultExportedData(
        {
          ...this.selectedSortObject,
          ...this.buildFilteredRequest(),
          type: filterType,
        },
      ).then(() => {
        this.exportInProgress = false;
      }).catch(() => {
        this.exportInProgress = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.button-area {
  margin-bottom: 10px;
}

.main-content {
  display: flex;
  flex-direction: column;

  .filter {
    width: 100%;
  }

  .data {
    flex: 1;
  }
}
</style>
